import { isFullChatListPath, isFullChatPath } from './excluded-paths'

/**
 * What the chat panel shows inside its column:
 *
 *   - `thread` — the active conversation (tab strip + `CanvasChatThread`).
 *   - `list`   — the recent-chats overview (`ChatListOverview`).
 *
 * On the full surfaces the PATH decides (`/chats` → list, `/chat/<id>` →
 * thread) so the URL stays the single source of truth there. In split mode
 * the panel shows `splitView` — the persisted view (the cookie below) or
 * its `?chatView=list` URL override (`./chat-panel-view.ts`).
 */
export type ChatPanelView = 'thread' | 'list'

export function resolveChatPanelView(args: {
  pathname: string | null | undefined
  splitView: ChatPanelView
}): ChatPanelView {
  if (isFullChatListPath(args.pathname)) {
    return 'list'
  }
  if (isFullChatPath(args.pathname)) {
    return 'thread'
  }
  return args.splitView
}

/**
 * Search param that opts the SPLIT-mode chat panel into the list view.
 * Read/written through `useChatPanelView` (`./chat-panel-view.ts`); the
 * constants live here so pure path builders can use them without nuqs.
 */
export const CHAT_VIEW_PARAM = 'chatView'
export const CHAT_VIEW_LIST = 'list'

/**
 * `path` with `?chatView=list` set — the split-route URL whose side panel
 * opens on the recent-chats overview. Used when the full-width `/chats`
 * surface collapses to the side so the user keeps looking at the list
 * rather than snapping back to a thread. Preserves any existing query.
 */
export function withChatListView(path: string): string {
  const url = new URL(path, 'http://brew.local')
  url.searchParams.set(CHAT_VIEW_PARAM, CHAT_VIEW_LIST)
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Persisted panel VIEW (`thread` | `list`) for the split-mode chat panel.
 *
 * A COOKIE, like `brew_chat_side`, because the view decides which header
 * and body the panel renders: a client-only read would have the server
 * paint the thread and the client hydrate the list (structural mismatch +
 * a visible swap). The server shell reads it and seeds `GlobalChatProvider`,
 * so the first paint is already on the user's view. Written on every
 * explicit switch (sidebar tab, back arrow, picking a chat) and adopted
 * from the full surfaces (`/chats` → list, `/chat/<id>` → thread), so the
 * list survives navigation and reload exactly like the active chat does.
 */
export const CHAT_VIEW_COOKIE_NAME = 'brew_chat_view'

/** One year, refreshed on every switch. */
export const CHAT_VIEW_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export const DEFAULT_CHAT_PANEL_VIEW: ChatPanelView = 'thread'

export function isChatPanelView(
  value: string | null | undefined
): value is ChatPanelView {
  return value === 'thread' || value === 'list'
}

/** Normalize a raw cookie value (server or client); garbage → thread. */
export function resolveStoredChatView(
  value: string | null | undefined
): ChatPanelView {
  return isChatPanelView(value) ? value : DEFAULT_CHAT_PANEL_VIEW
}

/** Client-side read — the fallback for mounts without a server seed. */
export function readChatViewCookie(): ChatPanelView {
  if (typeof document === 'undefined') {
    return DEFAULT_CHAT_PANEL_VIEW
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CHAT_VIEW_COOKIE_NAME}=([^;]*)`)
  )
  return resolveStoredChatView(match?.[1])
}

export function writeChatViewCookie(view: ChatPanelView): void {
  if (typeof document === 'undefined') {
    return
  }
  // biome-ignore lint/suspicious/noDocumentCookie: same raw write as `chat-side.ts` — a preference cookie the server shell reads back
  document.cookie = `${CHAT_VIEW_COOKIE_NAME}=${view}; path=/; max-age=${CHAT_VIEW_COOKIE_MAX_AGE}; samesite=lax`
}

/**
 * The `?chatView=list` URL override read off `window.location` at call
 * time — for handlers that must act on the view without subscribing to
 * search-param changes (the provider's `expandToFull`, fired from a resize
 * gesture). `null` on the server and when the URL carries no override.
 */
export function readChatViewParamFromLocation(): ChatPanelView | null {
  if (typeof window === 'undefined') {
    return null
  }
  const value = new URLSearchParams(window.location.search).get(CHAT_VIEW_PARAM)
  return value === CHAT_VIEW_LIST ? 'list' : null
}
